/**
 * Drawing helpers for the M0 placeholder art. Everything is vector-drawn
 * with Pixi Graphics and baked into atlases by the engine's AtlasBuilder.
 * Real art (docs/60, docs/61) replaces this module wholesale; the texture
 * keys and anchors stay the same.
 */
/** Warm Enugu palette: laterite earth, zinc greys, painted cement. */
export const PAL = {
    laterite: 0xb4532c,
    lateriteDark: 0x8f3f22,
    lateriteLight: 0xc96a3d,
    dust: 0xd08a5a,
    cement: 0xb9ae9c,
    cementDark: 0x968b7a,
    roomTile: 0xd9c8a8,
    asphalt: 0x4b4744,
    asphaltLight: 0x605a55,
    kerb: 0xb3ab9d,
    gutter: 0x3a3029,
    grass: 0x6e8c3a,
    grassDark: 0x55702b,
    market: 0x9b5634,
    zinc: 0x9aa3a8,
    zincDark: 0x6f787d,
    rust: 0x8a4a2a,
    wood: 0x7a4b2a,
    woodLight: 0x9c6638,
    plasticWhite: 0xeeeae2,
    plasticRed: 0xc9372c,
    drumBlue: 0x2f5fa8,
    kekeYellow: 0xe8b520,
    kekeGreen: 0x2f7d4a,
    black: 0x1b1714,
    wallCream: 0xe2d2ab,
    wallBlue: 0x8ab9bd,
    wallPink: 0xdba79e,
    wallBlock: 0xa49d92,
    dirtBand: 0x9b4d2c,
};
/** Multiplies RGB by f (clamped). */
export function shade(color, f) {
    const r = Math.min(255, Math.round(((color >> 16) & 255) * f));
    const g = Math.min(255, Math.round(((color >> 8) & 255) * f));
    const b = Math.min(255, Math.round((color & 255) * f));
    return (r << 16) | (g << 8) | b;
}
export function mix(a, b, t) {
    const r = ((a >> 16) & 255) * (1 - t) + ((b >> 16) & 255) * t;
    const g = ((a >> 8) & 255) * (1 - t) + ((b >> 8) & 255) * t;
    const bl = (a & 255) * (1 - t) + (b & 255) * t;
    return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(bl);
}
/** Deterministic PRNG so the art is identical on every load. */
export function rng(seed) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** Face shading convention: +y faces (screen lower-left) 0.9, +x faces (lower-right) 0.72, tops 1.06. */
export const SHADE_LEFT = 0.9;
export const SHADE_RIGHT = 0.72;
export const SHADE_TOP = 1.06;
/**
 * Projects local tile coordinates (x, y in tiles from the footprint's top
 * corner, z in PX upward) into frame coordinates.
 */
export class IsoPen {
    g;
    ox;
    oy;
    constructor(g, ox, oy) {
        this.g = g;
        this.ox = ox;
        this.oy = oy;
    }
    px(x, y, _z = 0) {
        return this.ox + (x - y) * 32;
    }
    py(x, y, z) {
        return this.oy + (x + y) * 16 - z;
    }
    /** Flat polygon through tile-space points [x, y, z, ...]. */
    poly(pts, color, alpha = 1) {
        const out = [];
        for (let i = 0; i < pts.length; i += 3)
            out.push(this.px(pts[i], pts[i + 1], pts[i + 2]), this.py(pts[i], pts[i + 1], pts[i + 2]));
        this.g.poly(out, true).fill({ color, alpha });
        return this;
    }
    /** Horizontal quad (rug, table top, roof sheet) at height z. */
    top(x, y, z, w, h, color, alpha = 1) {
        return this.poly([x, y, z, x + w, y, z, x + w, y + h, z, x, y + h, z], color, alpha);
    }
    /**
     * Box at (x, y, z) with footprint w x h tiles and height d px; draws the
     * three camera-facing faces with the standard shading.
     */
    box(x, y, z, w, h, d, color, opts = {}) {
        // +y face (lower-left)
        this.poly([x, y + h, z, x + w, y + h, z, x + w, y + h, z + d, x, y + h, z + d], shade(color, SHADE_LEFT));
        // +x face (lower-right)
        this.poly([x + w, y, z, x + w, y + h, z, x + w, y + h, z + d, x + w, y, z + d], shade(color, SHADE_RIGHT));
        this.top(x, y, z + d, w, h, shade(color, SHADE_TOP));
        if (opts.edge !== undefined) {
            const g = this.g;
            g.moveTo(this.px(x, y + h, z + d), this.py(x, y + h, z + d))
                .lineTo(this.px(x + w, y + h, z + d), this.py(x + w, y + h, z + d))
                .lineTo(this.px(x + w, y, z + d), this.py(x + w, y, z + d))
                .stroke({ width: 0.75, color: opts.edge, alpha: 0.6 });
            g.moveTo(this.px(x + w, y + h, z), this.py(x + w, y + h, z))
                .lineTo(this.px(x + w, y + h, z + d), this.py(x + w, y + h, z + d))
                .stroke({ width: 0.75, color: opts.edge, alpha: 0.5 });
        }
        return this;
    }
    /** Upright cylinder centred at tile (cx, cy), radius r tiles, height d px. */
    cylinder(cx, cy, z, r, d, color) {
        const sx = this.px(cx, cy, 0);
        const sy = this.py(cx, cy, z);
        const rx = r * 45; // a ground circle of radius r tiles is ~45r px wide and ~22.6r px tall on screen
        const ry = r * 22.6;
        const g = this.g;
        g.rect(sx - rx, sy - d, rx * 2, d).fill(shade(color, 0.82));
        g.ellipse(sx, sy, rx, ry).fill(shade(color, 0.82));
        // A lighter band on the left half to read as round.
        g.rect(sx - rx, sy - d, rx * 0.8, d).fill({ color: shade(color, 0.95), alpha: 0.7 });
        g.ellipse(sx, sy - d, rx, ry).fill(shade(color, SHADE_TOP));
        return this;
    }
    /** Line between two tile-space points. */
    line(x0, y0, z0, x1, y1, z1, color, width = 1, alpha = 1) {
        this.g.moveTo(this.px(x0, y0, z0), this.py(x0, y0, z0)).lineTo(this.px(x1, y1, z1), this.py(x1, y1, z1)).stroke({ width, color, alpha, cap: 'round' });
        return this;
    }
}
/**
 * Builds an AtlasItem for an object with a w x h tile footprint and art up
 * to `heightPx` tall. The frame is (w+h)*32 wide; the anchor is the
 * bottom-centre of the footprint (vision §6).
 */
export function propItem(key, w, h, heightPx, draw, margin = 3) {
    const fw = (w + h) * 32 + margin * 2;
    const fh = (w + h) * 16 + heightPx + margin * 2;
    const ox = margin + h * 32;
    const oy = margin + heightPx;
    return {
        key,
        w: fw,
        h: fh,
        anchorX: ox + (w - h) * 16,
        anchorY: oy + (w + h) * 16,
        draw: (g) => draw(new IsoPen(g, ox, oy)),
    };
}
